import { makeStyles, TableCell, TableRow } from "@material-ui/core";
import React from "react";
import { GetTranslatedValue, TranslatedValue } from "../../../model/Shared/TranslatedValue";
import ExpertExamsSelect from "../ExpertExamsSelect";
import { ExamTemplateIndex } from "../../../model/Exam";
import { OptionType } from "../../common/customSelect/CustomSelect";
import { ActionMeta, ValueType } from "react-select";
import { ExpertRole, getExpertRoleTranslationKey } from "../../../data/constants";
import { GetTranslation } from "../../../translations/GetTranslation";
import { useIntl } from "react-intl";
import { rolesWithRequiredExamOrCourseTypeAssignements, rolesAssignedToCourseTypes } from "../ExpertConstants";
import CourseTypeSelect from "../../course/CourseTypeSelect";
import { CourseType } from "../../../model/Course";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import CustomButton from "../../common/buttons/CustomButton";
import TranslatedMessage from "../../translations/TranslatedMessage";

export type ExpertRoleExamsRowProps = {
  role: ExpertRole;
  selectedExamIds?: string[];
  selectedCourseTypes?: CourseType[];
  publishedExams: ExamTemplateIndex[];
  onExamsChange: (selectedElements: ValueType<OptionType>, actionMeta?: ActionMeta<OptionType>) => void;
  onCourseTypeChange: (selectedElements: ValueType<OptionType>, actionMeta?: ActionMeta<OptionType>) => void;
  onRoleDelete: (role: ExpertRole) => void;
  examSelectError: boolean;
};

export const useExamColumnStyles = makeStyles((theme) => ({
  noPaddingColumn: {
    padding: 0,
  },
  bold: {
    fontWeight: "bold",
  },
  deleteButton: {
    backgroundColor: "transparent",
    color: theme.palette.secondary.main,
    "&:hover": {
      backgroundColor: "transparent",
      color: theme.palette.secondary.main,
      border: "none",
      boxShadow: "none",
    },
  },
}));

export const ExpertRoleExamsRow: React.FunctionComponent<ExpertRoleExamsRowProps> = ({
  role,
  selectedExamIds,
  selectedCourseTypes,
  publishedExams,
  onExamsChange,
  onCourseTypeChange,
  onRoleDelete,
  examSelectError,
}) => {
  const classes = useExamColumnStyles();
  const intl = useIntl();
  const publishedExamsObject: Record<string, TranslatedValue> = {};
  publishedExams.forEach((exam) => (publishedExamsObject[exam.id] = exam.name));
  const examOptions = publishedExams!.map((exam) => ({
    label: GetTranslatedValue(exam.name),
    value: exam.id,
    payload: exam,
  }));
  const publishedExamIds = publishedExams.map((pe) => pe.id);

  const selectedExamsOptions = selectedExamIds
    ? [...selectedExamIds.filter((se) => publishedExamIds.includes(se))]
        .sort((a, b) => {
          return GetTranslatedValue(publishedExamsObject[a]).localeCompare(
            GetTranslatedValue(publishedExamsObject[b] || b)
          );
        })
        .map((examId) => ({
          value: examId,
          label: <span>{GetTranslatedValue(publishedExamsObject[examId])}</span>,
        }))
    : [];

  return (
    <React.Fragment>
      <TableRow>
        <TableCell className={classes.bold}>{GetTranslation(intl, getExpertRoleTranslationKey(role))}</TableCell>
        <TableCell className={classes.noPaddingColumn}>
          {rolesAssignedToCourseTypes.includes(role) ? (
            <CourseTypeSelect
              selectedCourseTypes={selectedCourseTypes || []}
              onCourseTypeSelectChange={onCourseTypeChange}
              mandatory={rolesWithRequiredExamOrCourseTypeAssignements.includes(role)}
              error={examSelectError}
            />
          ) : (
            <ExpertExamsSelect
              selectedExpertExams={selectedExamsOptions}
              expertExams={[...examOptions].sort((a, b) => a.label.localeCompare(b.label))}
              onExpertExamsSelectChange={onExamsChange}
              mandatory={rolesWithRequiredExamOrCourseTypeAssignements.includes(role)}
              error={examSelectError}
            />
          )}
        </TableCell>
        <TableCell />
        <TableCell align="right">
          <CustomButton
            tooltip={<TranslatedMessage id="expert.field.role.delete" />}
            onClick={() => onRoleDelete(role)}
            internalButtonClassName={classes.deleteButton}
            color="secondary"
          >
            <HighlightOffIcon />
          </CustomButton>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

export default ExpertRoleExamsRow;
